export type SubmittedMedia = {
	url: string
	type: 'IMAGE' | 'VIDEO'
	thumbnail?: string
}

const VIDEO_URL_PATTERN = /\.(mp4|m4v|mov|webm|avi|mkv)(?:$|[?#])/i

const normalizeMediaType = (type: unknown, url: string): SubmittedMedia['type'] => {
	if (typeof type === 'string' && type.toUpperCase() === 'VIDEO') {
		return 'VIDEO'
	}

	return VIDEO_URL_PATTERN.test(url) ? 'VIDEO' : 'IMAGE'
}

const toSubmittedMedia = (item: unknown): SubmittedMedia | null => {
	if (typeof item === 'string') {
		const url = item.trim()
		return url ? { url, type: normalizeMediaType(undefined, url) } : null
	}

	if (!item || typeof item !== 'object') return null
	const value = item as { url?: unknown; type?: unknown; thumbnail?: unknown }
	if (typeof value.url !== 'string' || !value.url.trim()) return null

	const url = value.url.trim()
	return {
		url,
		type: normalizeMediaType(value.type, url),
		...(typeof value.thumbnail === 'string' && value.thumbnail.trim()
			? { thumbnail: value.thumbnail.trim() }
			: {}),
	}
}

const normalizeMedia = (media: unknown): SubmittedMedia[] => {
	if (!Array.isArray(media)) return []

	const seen = new Set<string>()
	return media.reduce<SubmittedMedia[]>((result, item) => {
		const normalized = toSubmittedMedia(item)
		if (!normalized || seen.has(normalized.url)) return result

		seen.add(normalized.url)
		result.push(normalized)
		return result
	}, [])
}

export const getSubmittedMedia = (report: any): SubmittedMedia[] => {
	const medias = normalizeMedia(report?.medias)
	return medias.length > 0 ? medias : normalizeMedia(report?.images)
}

export const getSubmittedMediaUrls = (report: any): string[] =>
	getSubmittedMedia(report).map((media) => media.url)

export const getTargetMedia = (target: any): SubmittedMedia[] => {
	const medias = normalizeMedia(target?.medias)
	if (medias.length > 0) return medias

	return normalizeMedia([
		target?.image,
		target?.thumbnail,
		...(Array.isArray(target?.thumbnails) ? target.thumbnails : []),
	])
}

export const getTargetPreviewMediaUrl = (target: any): string | null => {
	if (typeof target?.avatar === 'string' && target.avatar.trim()) {
		return target.avatar.trim()
	}

	const medias = getTargetMedia(target)
	return (
		medias.find((media) => media.type === 'IMAGE')?.url ||
		medias.find((media) => media.type === 'VIDEO' && media.thumbnail)?.thumbnail ||
		null
	)
}
